import backgroundBenchmark from "@images/backgroundBenchmark.png"
import errorInvitationShareCoach from "@images/error/errorInvitationShareCoach.svg"
import InvitationIcon from "@images/invitationIcon.svg"
import { ClockOutlineIcon } from "@images/svgIcons/clockOutlineIcon"
import { MessagesIcon } from "@images/svgIcons/messagesIcon"
import { CorrectIcon } from "@images/svgIcons/status/correctIcon"
import { ErrorIcon } from "@images/svgIcons/status/errorIcon"
import { useUser } from "@modules/auth/hooks/useUser"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { ExploreCard } from "@modules/explore/components/exploreCard/exploreCard"
import { ExploreProgramItemEntity } from "@modules/explore/entities/exploreEntity"
import { UserCell } from "@modules/members/components/userCell"
import { UserCellSkeleton } from "@modules/members/components/userCellSkeleton"
import { Path } from "@modules/navigation/routes"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { SharedCoachRequestAction } from "@modules/programs/programTypes"
import { SharedCoachRole } from "@modules/sharedCoach/sharedCoachDtos"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { HUButton } from "@modules/ui/components/huButton"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { Skeleton } from "primereact/skeleton"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTheme } from "styled-components"

export const InvitationDetailsInfo: React.FC<{}> = ({}) => {
  const user = useUser()
  const theme = useTheme()
  const navigate = useNavigate()
  const { hideSidebar } = useSidebar()
  const { localStorageService, programService } = useServicesContext()
  const programId = localStorageService.load(LocalStorageKeys.sharedCoachInvitation.programId, { value: "" })
  const { result: program, loading } = useProgram(programId.value)

  const [isRequest, setIsRequest] = useState(false)
  const toast = useToast()
  const { sharedCoach, isAuthor, isAcceptedSharedCoach } = useProgramRights(program || undefined)

  const openChatWithCoach = async () => {
    if (program?.author) {
      hideSidebar()
      localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)

      navigate(Path.Chat.Root, { state: { userToChatTo: program.author.id } })
    }
  }

  const requestSharedCoach = async (action: SharedCoachRequestAction) => {
    if (user?.id && program) {
      setIsRequest(true)
      await programService
        .requestSharedCoach(program.id, program._programType, action, user.id)
        .then(() => {
          toast.show({
            severity: "success",
            detail: word(`program.sharedCoach.action.request.${action}.success`),
          })
        })
        .catch(() => {
          toast.show({
            severity: "error",
            detail: word(`program.sharedCoach.action.request.error`),
          })
        })
        .finally(() => {
          hideSidebar()
          setIsRequest(false)
          localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
        })
    }
  }

  return (
    <div className="pb-4">
      {/* HEADER */}
      <div
        className="relative flex h-15rem bg-neutral-200 border-round-bottom-3xl pl-1"
        style={{ marginTop: "-85px", marginRight: "-20px", marginLeft: "-20px" }}
      >
        <div className="flex flex-column justify-content-center align-items-center w-6 h-full mt-2 ml-1">
          <div
            className="bg-contain bg-no-repeat bg-center"
            style={{ backgroundImage: `url('${InvitationIcon} ')`, width: "60px", height: "60px" }}
          ></div>
          {loading ? (
            <>
              <Skeleton width="80%" height="2rem" borderRadius="0.8rem" className="mt-1" />
              <Skeleton width="50%" height="1rem" borderRadius="0.8rem" className="mt-4" />
              <UserCellSkeleton className="flex flex-row mt-1 w-full justify-content-center" subtitle={true} />
            </>
          ) : program ? (
            <>
              <HUText fontStyle="HS" color={theme.neutralColor900} className="mt-1 pl-2" numberOfLine={2}>
                {program.title}
              </HUText>
              <HUText fontStyle="BS" color={theme.neutralColor700} className="mt-2">
                {word("invitationDetailsInfo.InvitationBy")}
              </HUText>
              <UserCell
                member={program.author}
                subtitle={program.author.username}
                className="flex gap-2 cursor-pointer mt-1"
              />
            </>
          ) : null}
        </div>
        <div
          className="bg-cover bg-no-repeat bg-right h-full w-6"
          style={{
            background: `url('${backgroundBenchmark}')`,
            clipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
            WebkitClipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
          }}
        ></div>
      </div>

      {program && (sharedCoach || isAuthor) ? (
        <>
          {/* TEXT */}
          <div className="flex flex-column w-full justify-content-center align-items-center text-center	mt-4 px-2">
            {isAuthor || isAcceptedSharedCoach ? (
              <>
                <HUText fontStyle="TM" color={theme.neutralColor700} className="mt-2">
                  {isAuthor ? word("invitationDetailsInfo.owner.title") : word("invitationDetailsInfo.coach.title")}
                </HUText>
                <HUText fontStyle="BM" color={theme.neutralColor700} className="mt-2">
                  {isAuthor
                    ? word("invitationDetailsInfo.owner.subtitle")
                    : word("invitationDetailsInfo.coach.subtitle")}
                </HUText>
              </>
            ) : (
              <HUText fontStyle="BM" color={theme.neutralColor700}>
                {word("invitationDetailsInfo.text")}
              </HUText>
            )}
          </div>
          {/* PROGRAM */}
          <div className="flex flex-column mt-4">
            <HUText type="label" color={theme.neutralColor700} fontStyle="LS">
              {word("invitationDetailsInfo.relevantProgram").toUpperCase()}
            </HUText>
            <div className="flex justify-content-center align-items-center mt-2">
              {loading ? (
                <Skeleton width="320px" height="300px" className="mt-1 rounded-33" />
              ) : (
                <ExploreCard
                  item={ExploreProgramItemEntity.fromProgram(program)}
                  disabled
                  onViewDetailsClick={() => {
                    null
                  }}
                  isSelected={false}
                />
              )}
            </div>
          </div>
          {/* PERMISSIONS  */}
          <div className="flex flex-column mt-4">
            <HuPermissions
              title={word("invitationDetailsInfo.permissions").toUpperCase()}
              role={SharedCoachRole.STANDARD}
            />
          </div>
          {/* BUTTONS */}
          {isAuthor || isAcceptedSharedCoach ? null : (
            <div className="flex justify-content-between flex-row w-full mt-4 gap-3">
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Quaternary"
                text={word("invitationDetailsInfo.refuse")}
                icon={{ icon: () => <ErrorIcon color={theme.mainWhite} width={20} height={20} /> }}
                className="w-full"
                onClick={async () => await requestSharedCoach(SharedCoachRequestAction.REFUSE)}
                disabled={loading || isRequest ? true : false}
              />
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Quaternary"
                text={word("invitationDetailsInfo.answerLater")}
                icon={{ icon: () => <ClockOutlineIcon color={theme.neutralColor900} width={14} height={14} /> }}
                className="w-full"
                onClick={() => {
                  hideSidebar()
                  localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
                }}
                disabled={loading || isRequest ? true : false}
              />
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Primary"
                text={word("invitationDetailsInfo.accept")}
                icon={{ icon: () => <CorrectIcon color={theme.mainWhite} width={20} height={20} /> }}
                className="w-full"
                onClick={async () => await requestSharedCoach(SharedCoachRequestAction.ACCEPT)}
                disabled={loading || isRequest ? true : false}
              />
            </div>
          )}
        </>
      ) : (
        <>
          {loading ? (
            <Skeleton width="100%" height="26rem" className="mt-6 rounded-33" />
          ) : (
            <>
              {/* GESTION OF ERROR  & LOADING*/}
              <div className="flex flex-column mt-6 jutify-content-center align-items-center bg-neutral-100 rounded-33">
                <HUText fontStyle="TM" color={theme.neutralColor900} className="mt-6">
                  {word("invitationDetailsInfo.notValid.title")}
                </HUText>
                <HUText fontStyle="BM" color={theme.neutralColor900} className="mt-3 px-4 text-center">
                  {word("invitationDetailsInfo.notValid.subtitle")}
                </HUText>
                <div
                  className="flex w-full h-12rem bg-contain bg-no-repeat bg-center my-8 "
                  style={{ backgroundImage: `url(${errorInvitationShareCoach})` }}
                ></div>
                {program?.author && (
                  <HUButton
                    type="Right_Icon"
                    size="M"
                    colorType="Secondary"
                    text={word("invitationDetailsInfo.sendMessageToCoach")}
                    className="mb-4"
                    onClick={openChatWithCoach}
                    icon={{ iconView: <MessagesIcon color={theme.mainBlack} width={16} height={16} /> }}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
